/* oxlint-disable no-param-reassign */

'use client';

import { useBunja } from 'bunja/react';
import html2canvas from 'html2canvas';
import { useAtom, useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import { useRef } from 'react';

import { TradingLeaderboardInterval } from '@flipster/apis/v2/trading-leaderboards/types';
import { Modal } from '@flipster/react/design-system-legacy/modal';
import { Button } from '@flipster/react/design-system/button';
import { createSingleChoice } from '@flipster/react/design-system/chip';
import { snackbar } from '@flipster/react/design-system/snackbar';
import { Spinner } from '@flipster/react/design-system/spinner';
import { createTabsComponent } from '@flipster/react/design-system/tabs';
import { css } from '@flipster/styled-system/css';
import { center } from '@flipster/styled-system/patterns';
import { downloadImageFile } from '@flipster/utils/file';
import segment from '@flipster/utils/segment';
import { TradingLeaderboardEvent } from '@flipster/utils/segment/events/trading-leaderboard';

import { useAuth } from '../../../shared/auth/state';
import { useReferralCode } from '../../../shared/referral-code/state';
import { getFlipsterUrl } from '../../../shared/urls';
import { useCopy } from '../../../shared/webview/clipboard';
import { rankShareBunja } from './bunja';
import { RankShareCard } from './card';
import { NotRankedYet } from './not-ranked-yet';

type RankShareLeagueOption = 'ALL' | 'LEAGUE';
const Tabs = createTabsComponent<TradingLeaderboardInterval>();
const Choice = createSingleChoice<RankShareLeagueOption>();

interface RankShareModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const RankShareModal = ({ isOpen, onClose }: RankShareModalProps) => {
  const t = useTranslations();
  const { isLoggedIn } = useAuth();
  const imageRef = useRef<HTMLDivElement>(null);
  const copy = useCopy('Referral link copied');
  const { referralCode } = useReferralCode({ enabled: isLoggedIn && isOpen });

  const { isPendingAtom, rankingAtom, leagueOptionAtom, intervalAtom } = useBunja(rankShareBunja);
  const isPending = useAtomValue(isPendingAtom);
  const ranking = useAtomValue(rankingAtom);

  const [leagueOption, setLeagueOption] = useAtom(leagueOptionAtom);
  const [interval, setInterval] = useAtom(intervalAtom);

  const copyUrl = `${getFlipsterUrl()}/signup?referral_code=${referralCode ?? ''}&referral_type=copy`;

  const handleDownloadImage = async () => {
    if (!imageRef.current || !ranking) return;
    segment.track(TradingLeaderboardEvent.LeaderboardPerformanceDownloadClick, {
      interval,
      tier: ranking.tier,
      ranking: ranking.rank,
      league_type: leagueOption === 'ALL' ? 'all' : 'myleague',
    });

    try {
      const canvas = await html2canvas(imageRef.current, {
        scale: 3,
        windowWidth: 343,
        useCORS: true,
        removeContainer: false,
      });

      const dataUrl = canvas.toDataURL('image/png');
      downloadImageFile('rank-share', dataUrl);
      snackbar.success({ message: 'Image saved' });
    } catch {
      snackbar.error({ message: 'Unable to save the image\nPlease try again' });
    }
  };

  const onClickCopy = () => {
    copy(copyUrl);
    if (!ranking) return;
    segment.track(TradingLeaderboardEvent.LeaderboardPerformanceShareClick, {
      interval,
      tier: ranking.tier,
      ranking: ranking.rank,
      league_type: leagueOption === 'ALL' ? 'all' : 'myleague',
    });
  };

  const rankShareAvailable = !isPending && ranking;
  const isUnranked = ranking?.rankingStatus === 'UNRANKED';

  return (
    <Modal size='small' isOpen={isOpen}>
      <Modal.Close onClose={onClose} />
      <Modal.Header>
        <Modal.Title label={t('Autogen_common.r251126.s_Share_my_rank')} />
      </Modal.Header>
      <Modal.Content css={{ pt: 16, px: 0 }}>
        <Tabs type='fixed' value={interval} onChange={setInterval}>
          <Tabs.Tab value='WEEKLY' label={t('Navigation.r250401.Weekly')} />
          <Tabs.Tab value='MONTHLY' label={t('Navigation.r250401.Monthly')} />
        </Tabs>
        <Choice value={leagueOption} onChange={setLeagueOption} css={{ p: 16 }}>
          <Choice.Chip label={t('Autogen_common.r251126.s_All')} value='ALL' />
          <Choice.Chip label={t('Autogen_common.r251126.s_My_league')} value='LEAGUE' />
        </Choice>
        <section
          className={css({
            maxW: 343,
            w: '100%',
            mx: 'auto',
          })}
        >
          <div className={css({ mt: 24 })} ref={imageRef}>
            {!rankShareAvailable ? (
              <div className={center({ h: 396, p: 24 })}>
                <Spinner />
              </div>
            ) : ranking.rankingStatus != 'UNRANKED' ? (
              <RankShareCard copyUrl={copyUrl} referralCode={referralCode} ranking={ranking} />
            ) : (
              <NotRankedYet onClose={onClose} css={{ h: 396 }} />
            )}
          </div>
        </section>
      </Modal.Content>
      {!isUnranked && (
        <Modal.Footer>
          <Button
            disabled={!rankShareAvailable}
            color='neutral'
            hierarchy='secondary'
            size='large'
            label={t('Autogen_common.r251126.s_Download')}
            withSpinner
            onClick={handleDownloadImage}
          />
          <Button size='large' label={t('Autogen_common.r251126.s_Copy_Link')} onClick={onClickCopy} />
        </Modal.Footer>
      )}
    </Modal>
  );
};
